/**
 * Settings modal — v0M15.4.
 *
 * Live-update settings screen shared between the Main Menu (Settings
 * button) and the in-game menu (v0M15.5). Two sections at v0M15.4:
 *
 *  - **Audio**: SFX volume + music volume + master mute.
 *    SFX changes propagate immediately through
 *    `settings-storage.subscribeSettings` → `audio.applyAudioSettings`.
 *    Music volume is persisted but not yet wired (no music system).
 *
 *  - **Autosave**: frequency dropdown (Off / 5min / 15min / 30min).
 *    Persisted; consumed by the v0M15.5 in-game autosave loop.
 *
 * Keybindings section deferred to v0M15.4b — that section has enough
 * surface area (per-action rebind + capture flow) to warrant its own
 * milestone.
 *
 * UX choice: no explicit Save button. Every control mutates persisted
 * settings on change (live update). Sole action is Close. Rationale in
 * `settings-storage.ts` header.
 */

import { injectMenuStyles } from './menu-styles';
import {
  AUTOSAVE_FREQUENCY_LABELS,
  DEFAULT_KEYBINDINGS,
  KEYBINDABLE_ACTION_LABELS,
  loadSettings,
  updateSettings,
} from '../settings-storage';
import type { AutosaveFrequency, KeybindableAction, Keybinding } from '../settings-storage';

export interface SettingsModalCallbacks {
  /** Called when the user clicks Close, hits Escape, or clicks the panel backdrop. */
  onClose(): void;
}

export interface SettingsModalHandle {
  /** Tear down the overlay + remove the Escape handler. Idempotent. */
  destroy(): void;
}

/**
 * Mount the Settings modal into `root`. The overlay reuses the shared
 * menu overlay classes so it sits at the same z-index (1000) as the
 * main menu — mounting it above an already-open menu is fine; Close
 * simply unmounts this modal and leaves the underlying menu visible.
 */
export function mountSettingsModal(
  root: HTMLElement,
  cb: SettingsModalCallbacks,
): SettingsModalHandle {
  injectMenuStyles();

  const overlay = document.createElement('div');
  overlay.className = 'su-menu-overlay';
  root.appendChild(overlay);

  const panel = document.createElement('div');
  panel.className = 'su-menu-panel';
  overlay.appendChild(panel);

  const escHandler = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      cb.onClose();
    }
  };
  window.addEventListener('keydown', escHandler);

  // Snapshot of the current settings for value initialisation. We do
  // NOT hold onto this — every control reads `loadSettings()` fresh on
  // change so we don't diverge from a settings mutation that happened
  // in another modal since mount (defensive; unlikely at v0M15.4).
  const initial = loadSettings();

  render(panel, initial);

  let destroyed = false;
  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      window.removeEventListener('keydown', escHandler);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    },
  };

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  function render(host: HTMLElement, s: ReturnType<typeof loadSettings>): void {
    host.innerHTML = '';

    const title = document.createElement('h1');
    title.className = 'su-menu-title';
    title.textContent = 'Settings';
    host.appendChild(title);

    host.appendChild(renderAudioSection(s));
    host.appendChild(renderAutosaveSection(s));
    host.appendChild(renderKeybindingsSection(s, host));

    // Footer — Close button only.
    const footer = document.createElement('div');
    footer.className = 'su-save-picker-footer';
    host.appendChild(footer);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'su-save-picker-btn su-primary';
    closeBtn.textContent = 'Close';
    closeBtn.onclick = (): void => cb.onClose();
    footer.appendChild(closeBtn);
  }

  function renderAudioSection(s: ReturnType<typeof loadSettings>): HTMLElement {
    const section = document.createElement('div');
    section.className = 'su-settings-section';

    const h = document.createElement('h2');
    h.className = 'su-settings-section-title';
    h.textContent = 'Audio';
    section.appendChild(h);

    // SFX volume — reflects immediately via settings-storage subscribe.
    const sfxValueSpan = document.createElement('span');
    sfxValueSpan.className = 'su-settings-row-value';
    sfxValueSpan.textContent = pctLabel(s.audio.sfxVolume);
    const sfxSlider = makeVolumeSlider(s.audio.sfxVolume, (v) => {
      sfxValueSpan.textContent = pctLabel(v);
      updateSettings({ audio: { sfxVolume: v } });
    });
    section.appendChild(makeRow('SFX Volume', sfxSlider, sfxValueSpan));

    // Music volume — persisted only; no music consumer at v0M15.4.
    const musicValueSpan = document.createElement('span');
    musicValueSpan.className = 'su-settings-row-value';
    musicValueSpan.textContent = pctLabel(s.audio.musicVolume);
    const musicSlider = makeVolumeSlider(s.audio.musicVolume, (v) => {
      musicValueSpan.textContent = pctLabel(v);
      updateSettings({ audio: { musicVolume: v } });
    });
    section.appendChild(makeRow('Music Volume', musicSlider, musicValueSpan));

    // Muted checkbox — disables both sliders visually via .disabled.
    const mutedCb = document.createElement('input');
    mutedCb.type = 'checkbox';
    mutedCb.className = 'su-settings-checkbox';
    mutedCb.checked = s.audio.muted;
    mutedCb.addEventListener('change', () => {
      updateSettings({ audio: { muted: mutedCb.checked } });
      sfxSlider.disabled = mutedCb.checked;
      musicSlider.disabled = mutedCb.checked;
    });
    sfxSlider.disabled = s.audio.muted;
    musicSlider.disabled = s.audio.muted;
    section.appendChild(makeRow('Mute', mutedCb, document.createElement('span')));

    const musicNote = document.createElement('div');
    musicNote.className = 'su-settings-note';
    musicNote.textContent = '(Music slider is persisted; no music track yet at v0M15.4.)';
    section.appendChild(musicNote);

    return section;
  }

  // -------------------------------------------------------------------------
  // v0M15.4b (2026-07-30) — Keybindings section
  // -------------------------------------------------------------------------

  /**
   * Render the Keybindings section with one row per rebindable action.
   * Each row: [action label] [current-key badge] [Rebind] [Reset].
   * Rebind opens a "Press any key…" capture overlay that intercepts the
   * next non-Escape keydown and writes it to settings; Escape cancels
   * capture without changing the binding. Section footer has a "Reset all"
   * button that reverts every action to DEFAULT_KEYBINDINGS.
   */
  function renderKeybindingsSection(
    s: ReturnType<typeof loadSettings>,
    host: HTMLElement,
  ): HTMLElement {
    const section = document.createElement('div');
    section.className = 'su-settings-section';

    const h = document.createElement('h2');
    h.className = 'su-settings-section-title';
    h.textContent = 'Keybindings';
    section.appendChild(h);

    // Track per-action badge nodes so we can update them in place after
    // a rebind / reset without re-rendering the whole section.
    const badges = new Map<KeybindableAction, HTMLSpanElement>();

    const actions: KeybindableAction[] = ['pause', 'openMenu', 'fastPan'];
    for (const action of actions) {
      const row = document.createElement('div');
      row.className = 'su-settings-row';

      const lbl = document.createElement('div');
      lbl.className = 'su-settings-row-label';
      lbl.textContent = KEYBINDABLE_ACTION_LABELS[action];
      row.appendChild(lbl);

      const badge = document.createElement('span');
      badge.className = 'su-keybind-badge';
      badge.textContent = s.keybindings[action].label;
      row.appendChild(badge);
      badges.set(action, badge);

      const rebindBtn = document.createElement('button');
      rebindBtn.className = 'su-save-picker-btn';
      rebindBtn.textContent = 'Rebind';
      rebindBtn.onclick = (): void => {
        openRebindCapture(action, (captured) => {
          updateSettings({ keybindings: { [action]: captured } as Partial<typeof s.keybindings> });
          badge.textContent = captured.label;
        }, host);
      };
      row.appendChild(rebindBtn);

      const resetBtn = document.createElement('button');
      resetBtn.className = 'su-save-picker-btn';
      resetBtn.textContent = 'Reset';
      resetBtn.title = `Restore default (${DEFAULT_KEYBINDINGS[action].label})`;
      resetBtn.onclick = (): void => {
        const def = DEFAULT_KEYBINDINGS[action];
        updateSettings({ keybindings: { [action]: def } as Partial<typeof s.keybindings> });
        badge.textContent = def.label;
      };
      row.appendChild(resetBtn);

      section.appendChild(row);
    }

    // Section footer — Reset all.
    const footerRow = document.createElement('div');
    footerRow.className = 'su-settings-row';
    const spacer = document.createElement('div');
    spacer.className = 'su-settings-row-label';
    footerRow.appendChild(spacer);
    const resetAllBtn = document.createElement('button');
    resetAllBtn.className = 'su-save-picker-btn';
    resetAllBtn.textContent = 'Reset All';
    resetAllBtn.onclick = (): void => {
      updateSettings({ keybindings: { ...DEFAULT_KEYBINDINGS } });
      for (const action of actions) {
        badges.get(action)!.textContent = DEFAULT_KEYBINDINGS[action].label;
      }
    };
    footerRow.appendChild(resetAllBtn);
    section.appendChild(footerRow);

    const note = document.createElement('div');
    note.className = 'su-settings-note';
    note.textContent = 'Rebindable at v1: Pause, Open Menu, Fast Pan (hold). Escape cancels a rebind in progress.';
    section.appendChild(note);

    return section;
  }

  function renderAutosaveSection(s: ReturnType<typeof loadSettings>): HTMLElement {
    const section = document.createElement('div');
    section.className = 'su-settings-section';

    const h = document.createElement('h2');
    h.className = 'su-settings-section-title';
    h.textContent = 'Autosave';
    section.appendChild(h);

    const select = document.createElement('select');
    select.className = 'su-menu-select';
    for (const freq of ['off', '5min', '15min', '30min'] as const) {
      const opt = document.createElement('option');
      opt.value = freq;
      opt.textContent = AUTOSAVE_FREQUENCY_LABELS[freq];
      if (freq === s.autosave.frequency) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener('change', () => {
      updateSettings({ autosave: { frequency: select.value as AutosaveFrequency } });
    });
    section.appendChild(makeRow('Frequency', select, document.createElement('span')));

    const note = document.createElement('div');
    note.className = 'su-settings-note';
    note.textContent = 'Autosave writes to the reserved Autosave slot in Load Game.';
    section.appendChild(note);

    return section;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeRow(label: string, control: HTMLElement, value: HTMLElement): HTMLElement {
  const row = document.createElement('div');
  row.className = 'su-settings-row';
  const lbl = document.createElement('div');
  lbl.className = 'su-settings-row-label';
  lbl.textContent = label;
  row.appendChild(lbl);
  row.appendChild(control);
  row.appendChild(value);
  return row;
}

function makeVolumeSlider(initial: number, onChange: (v: number) => void): HTMLInputElement {
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'su-settings-slider';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.value = String(Math.round(initial * 100));
  slider.addEventListener('input', () => {
    const v = parseInt(slider.value, 10) / 100;
    onChange(v);
  });
  return slider;
}

function pctLabel(v: number): string {
  return `${Math.round(v * 100)}%`;
}

// ---------------------------------------------------------------------------
// v0M15.4b — Rebind capture flow
// ---------------------------------------------------------------------------

/**
 * Open a "Press any key…" capture overlay on top of the Settings modal.
 * Intercepts the next keydown (except Escape, which cancels + closes).
 *
 * Rationale for intercept semantics:
 *  - Bare modifier press (ShiftLeft alone) is a valid binding.
 *  - Escape cancels — matches the modal-close convention throughout the
 *    game and prevents a user accidentally locking themselves out of the
 *    in-game menu by binding openMenu to a key they can't reach.
 *  - Capture uses window `keydown` at capture-phase so it wins over the
 *    outer Settings modal's own Escape handler while the capture is
 *    active. Cleanup removes the listener and the overlay.
 */
function openRebindCapture(
  _action: KeybindableAction,
  onCaptured: (kb: Keybinding) => void,
  host: HTMLElement,
): void {
  // `_action` reserved for future per-action capture policy (e.g. openMenu
  // could reject binding to F-keys the browser reserves). Kept in signature
  // for API stability; unused at v1.
  const overlay = document.createElement('div');
  overlay.className = 'su-menu-overlay';
  overlay.style.zIndex = '1100'; // above the Settings modal (1000).

  const panel = document.createElement('div');
  panel.className = 'su-menu-panel';
  panel.style.maxWidth = '360px';
  panel.style.textAlign = 'center';

  const msg = document.createElement('div');
  msg.style.fontSize = '18px';
  msg.style.margin = '20px 0';
  msg.textContent = 'Press any key…';
  panel.appendChild(msg);

  const cancelNote = document.createElement('div');
  cancelNote.className = 'su-settings-note';
  cancelNote.textContent = 'Escape to cancel';
  panel.appendChild(cancelNote);

  overlay.appendChild(panel);
  host.appendChild(overlay);

  const handler = (e: KeyboardEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'Escape') {
      cleanup();
      return;
    }
    onCaptured({ code: e.code, label: labelForCode(e.code) });
    cleanup();
  };
  const cleanup = (): void => {
    window.removeEventListener('keydown', handler, true);
    if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
  };
  // Capture-phase listener so we win over the Settings modal's own
  // bubble-phase Escape handler.
  window.addEventListener('keydown', handler, true);
}

/**
 * Human-readable label for a `KeyboardEvent.code`. Handles the common
 * keys used at v1 rebind time; falls back to the raw code string when
 * unknown. See MDN for the full `.code` catalogue.
 */
function labelForCode(code: string): string {
  const map: Readonly<Record<string, string>> = {
    Space: 'Space',
    Escape: 'Esc',
    Enter: 'Enter',
    Tab: 'Tab',
    Backspace: 'Backspace',
    ShiftLeft: 'Left Shift',
    ShiftRight: 'Right Shift',
    ControlLeft: 'Left Ctrl',
    ControlRight: 'Right Ctrl',
    AltLeft: 'Left Alt',
    AltRight: 'Right Alt',
    MetaLeft: 'Left Meta',
    MetaRight: 'Right Meta',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Backquote: '`',
    CapsLock: 'Caps Lock',
  };
  if (code in map) return map[code]!;
  // Keys `KeyA` .. `KeyZ` → single letter.
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  // Digits `Digit0` .. `Digit9` → single digit.
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  // Function keys `F1` .. `F12` — pass through.
  if (/^F\d{1,2}$/.test(code)) return code;
  // Fallback — expose the raw code so the user can at least identify it.
  return code;
}
